import React, { createContext, useContext, useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { identifyUser, resetAnalyticsUser } from '@/lib/analytics';
import { isMainHost } from '@/utils/lexDomain';

// Cookie-Auth (T1, Juni 2026): API ist same-origin → httpOnly access_token-Cookie
// wird automatisch mitgesendet. withCredentials global für etwaige cross-origin Setups.
axios.defaults.withCredentials = true;

// Gast Read-only (Juni 2026): Writes auf Demo-Entitäten/Bereiche liefern 403 mit
// detail.code === "guest_readonly" → globales Event, damit der "Konto sichern"-Moment
// erscheint (GuestBanner hört darauf). Andere Fehler bleiben unberührt.
axios.interceptors.response.use(
  (res) => res,
  (error) => {
    const detail = error?.response?.data?.detail;
    if (error?.response?.status === 403 && detail && detail.code === 'guest_readonly') {
      try { window.dispatchEvent(new CustomEvent('ivoliq:guest-readonly', { detail })); } catch { /* ignore */ }
    }
    return Promise.reject(error);
  }
);

const AuthContext = createContext(null);
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authToken, setAuthToken] = useState(null);
  const [error, setError] = useState(null);

  // Auto-logout after 60 minutes of inactivity
  const INACTIVITY_TIMEOUT = 60 * 60 * 1000; // 60 minutes in milliseconds
  const WARNING_TIMEOUT = 50 * 60 * 1000; // Show warning at 50 minutes (10 min before logout)
  const inactivityTimerRef = useRef(null);
  const warningTimerRef = useRef(null);
  const [showInactivityWarning, setShowInactivityWarning] = useState(false);

  // Tax residency and location tracking
  const [showTaxResidencyModal, setShowTaxResidencyModal] = useState(false);
  const [showLocationChangeModal, setShowLocationChangeModal] = useState(false);
  const [detectedCountry, setDetectedCountry] = useState(null);
  const [locationCheckData, setLocationCheckData] = useState(null);
  const [taxResidencyConfirmed, setTaxResidencyConfirmed] = useState(false); // Local flag to prevent modal reopening

  // Initialize auth via httpOnly cookie (T1): kein Token mehr im localStorage.
  useEffect(() => {
    checkAuth();
  }, []);

  // Setup inactivity tracking when user is authenticated
  useEffect(() => {
    // Only setup timer when user logs in/out (user.id changes)
    // NOT on every user property change (prevents unnecessary resets)
    if (!user?.id) {
      // Clear timers if user logs out
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      setShowInactivityWarning(false);
      return;
    }

    // Gast-Accounts: KEIN Auto-Logout bei Inaktivität. Gäste sollen ihre Demo
    // jederzeit fortsetzen können; die Daten werden ohnehin erst nach 3 Tagen
    // serverseitig aufgeräumt. Ein Auto-Logout würde sie sonst aussperren.
    if (user?.is_guest) {
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      setShowInactivityWarning(false);
      return;
    }

    console.log('[InactivityTimer] Initializing for user:', user.id);

    // Function to reset inactivity timer
    const resetInactivityTimer = () => {
      // Clear existing timers
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }

      // Hide warning if shown
      setShowInactivityWarning(false);

      // Set warning timer (10 minutes before logout)
      warningTimerRef.current = setTimeout(() => {
        console.log('[InactivityTimer] User will be logged out in 10 minutes due to inactivity');
        setShowInactivityWarning(true);
      }, WARNING_TIMEOUT);

      // Set logout timer (60 minutes)
      inactivityTimerRef.current = setTimeout(() => {
        console.log('[InactivityTimer] User inactive for 60 minutes - logging out');
        setShowInactivityWarning(false);
        logout();
      }, INACTIVITY_TIMEOUT);
    };

    // Activity events to track
    const events = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart', 'click'];

    // Add event listeners
    events.forEach(event => {
      document.addEventListener(event, resetInactivityTimer, { passive: true });
    });

    // Initialize timer
    resetInactivityTimer();

    // Cleanup function
    return () => {
      console.log('[InactivityTimer] Cleaning up for user:', user.id);
      
      // Clear timers
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
        inactivityTimerRef.current = null;
      }
      if (warningTimerRef.current) {
        clearTimeout(warningTimerRef.current);
        warningTimerRef.current = null;
      }
      
      // Remove event listeners
      events.forEach(event => {
        document.removeEventListener(event, resetInactivityTimer, { passive: true });
      });
    };
  }, [user?.id]); // Only re-run when user ID changes (login/logout)

  // Function to extend session (user acknowledges warning)
  const extendSession = () => {
    setShowInactivityWarning(false);
    // This will trigger a reset of all timers through the activity
    document.dispatchEvent(new Event('mousedown'));
  };

  const checkAuth = async () => {
    console.log('[AuthProvider] Checking authentication via cookie...');
    setLoading(true);
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      // Strikte Produkt-Trennung (Sep 2026): Eine lex-Session hat auf der Hauptdomain
      // (ivoliq.io/dev.ivoliq.io) NICHTS verloren — sie wird hier verworfen (Cookie + Token),
      // OHNE Weiterleitung. Die lex-Anmeldung auf lex.ivoliq.io (eigene Origin) bleibt bestehen.
      if (isMainHost() && userData?.account_variant === 'lex') {
        console.warn('[AuthProvider] lex-Session auf Hauptdomain erkannt — wird verworfen.');
        try { await axios.post(`${API}/auth/logout`, {}); } catch (_) { /* ignore */ }
        try { localStorage.removeItem('auth_token'); } catch (_) { /* ignore */ }
        setAuthToken(null);
        setUser(null);
        setLoading(false);
        return null;
      }
      setUser(userData);
      identifyUser(userData);
      setAuthToken('cookie');
      console.log('[AuthProvider] ✓ User authenticated:', userData.email);

      let taxConfirmed = true;
      let effectiveCountry = userData.country;
      try {
        const countryResponse = await axios.get(`${API}/country/info`);
        taxConfirmed = countryResponse.data?.tax_residency_confirmed ?? true;
        effectiveCountry = countryResponse.data?.country_code || effectiveCountry;
      } catch (countryError) {
        console.error('[AuthProvider] Country info check failed:', countryError);
      }

      if (!taxConfirmed && !taxResidencyConfirmed) {
        setDetectedCountry(effectiveCountry);
        setShowTaxResidencyModal(true);
      } else {
        checkLocationChange();
      }

      setLoading(false);
      return userData;
    } catch (error) {
      console.error('[AuthProvider] ❌ Auth check failed:', error.message);
      setAuthToken(null);
      setUser(null);
      setLoading(false);
    }
  };

  const checkLocationChange = async () => {
    try {
      const response = await axios.get(`${API}/country/check-location-change`);
      const data = response.data;
      
      if (data.location_changed) {
        setLocationCheckData(data);
        setShowLocationChangeModal(true);
      }
    } catch (error) {
      console.error('Location check failed:', error);
    }
  };

  const register = async (email, password, name, referralSource = null, extras = {}) => {
    const response = await axios.post(`${API}/auth/register`, {
      email,
      password,
      name,
      referral_source: referralSource,
      variant: extras.variant || null,
      accepted_terms: extras.accepted_terms === true,
      accepted_privacy: extras.accepted_privacy === true,
      accepted_avv: extras.accepted_avv === true,
      invite_code: extras.invite_code || null,
    });
    // Soft-Gate (Juni 2026): Backend loggt direkt per httpOnly-Cookie ein
    if (response.data?.auto_login) {
      const userData = await checkAuth();
      return { ...response.data, user: userData };
    }
    return response.data;
  };

  const login = async (email, password, rememberMe = false, variant = null) => {
    await axios.post(`${API}/auth/login`, {
      email,
      password,
      remember_me: rememberMe,
      // Aug 2026 — Produkt-Trennung: "lex" scoped den Login auf lex-Accounts
      ...(variant ? { variant } : {}),
    });
    // Token kommt als httpOnly-Cookie vom Backend — kein localStorage mehr.
    const userData = await checkAuth();
    return userData;
  };

  // ── Offener Einstieg (Gastmodus) ──────────────────────────────────────
  const startGuest = async (country = 'DE', persona = 'freelancer') => {
    const response = await axios.post(`${API}/guest/start`, { country, persona });
    try { if (response.data?.resume_token) localStorage.setItem('ivoliq_demo_resume', response.data.resume_token); } catch { /* ignore */ }
    const userData = await checkAuth();
    return { ...response.data, user: userData };
  };

  const switchGuestCountry = async (country) => {
    const response = await axios.post(`${API}/guest/switch-country`, { country });
    const userData = await checkAuth();
    return { ...response.data, user: userData };
  };

  const switchGuestPersona = async (persona) => {
    const response = await axios.post(`${API}/guest/switch-persona`, { persona });
    const userData = await checkAuth();
    return { ...response.data, user: userData };
  };

  const resumeGuest = async (token) => {
    const response = await axios.post(`${API}/guest/resume`, { token });
    try { localStorage.setItem('ivoliq_demo_resume', token); } catch { /* ignore */ }
    const userData = await checkAuth();
    return { ...response.data, user: userData };
  };

  const sendResumeEmail = async (email, farewell = false) => {
    const response = await axios.post(`${API}/guest/resume-email`, {
      email, base_url: window.location.origin, farewell,
    });
    return response.data;
  };

  const claimGuest = async ({ email, password, name = null, acceptedTerms = false }) => {
    const response = await axios.post(`${API}/guest/claim`, {
      email, password, name, accepted_terms: acceptedTerms === true,
    });
    const userData = await checkAuth();
    return { ...response.data, user: userData };
  };

  // Trichter-Messung: Gast-Ereignis stempeln (fire-and-forget)
  const logGuestEvent = async (event) => {
    try { await axios.post(`${API}/guest/event`, { event }); } catch { /* ignore */ }
  };

  const resendVerification = async (email) => {
    const response = await axios.post(`${API}/auth/resend-verification`, null, {
      params: { email }
    });
    return response.data;
  };

  const loginWithGoogle = async () => {
    try {
      // NEW: Call backend to get Google OAuth URL
      const response = await axios.get(`${API}/auth/google/login`);
      const authUrl = response.data.auth_url;
      
      console.log('[GoogleAuth] Redirecting to Google OAuth:', authUrl);
      
      // Redirect to Google OAuth
      window.location.href = authUrl;
    } catch (error) {
      console.error('[GoogleAuth] Failed to initiate Google login:', error);
      setError('Failed to connect to Google. Please try again.');
    }
  };

  const handleGoogleCallback = async () => {
    // NEW: Get authorization code from URL query params (standard OAuth2)
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
    const error = urlParams.get('error');

    console.log('[GoogleAuth] Callback URL:', window.location.href);
    console.log('[GoogleAuth] Authorization code:', code ? 'present' : 'missing');

    // Check for OAuth errors
    if (error) {
      console.error('[GoogleAuth] ❌ OAuth error:', error);
      setError(`Google login failed: ${error}`);
      setLoading(false);
      return false;
    }

    if (!code) {
      console.error('[GoogleAuth] ❌ No authorization code found in URL');
      console.error('[GoogleAuth] Expected format: /auth/callback?code=xxx');
      setLoading(false);
      return false;
    }

    try {
      console.log('[GoogleAuth] ✓ Authorization code found, exchanging for token...');
      
      // NEW: Exchange code for user data via backend
      const response = await axios.post(`${API}/auth/google/callback`, {
        code: code
      });
      
      console.log('[GoogleAuth] ✓ Backend response received');
      
      const userData = response.data.user;
      const isNewUser = response.data.is_new_user; // Backend should return this flag
      
      if (!userData) {
        console.error('[GoogleAuth] ❌ Missing user data in response');
        setLoading(false);
        return false;
      }
      
      console.log('[GoogleAuth] ✓ Authenticated user:', userData.email);
      console.log('[GoogleAuth] Is new user:', isNewUser);
      
      // Token ist als httpOnly-Cookie gesetzt — kein localStorage.
      setAuthToken('cookie');
      setUser(userData);
      let latestUserData = userData;
      
      // Store new user flag for onboarding
      if (isNewUser) {
        localStorage.setItem('show_onboarding', 'true');
      }
      
      // Clean URL immediately for better UX
      window.history.replaceState({}, document.title, window.location.pathname);
      console.log('[GoogleAuth] ✓ URL cleaned');
      
      // Fetch full user profile with tax residency check (async, non-blocking)
      try {
        console.log('[GoogleAuth] Loading full profile...');
        const profileResponse = await axios.get(`${API}/auth/me`);
        const fullUserData = profileResponse.data;
        setUser(fullUserData);
        latestUserData = fullUserData;
        console.log('[GoogleAuth] ✓ Full profile loaded');
        
        // Check tax residency if needed
        if (!fullUserData.tax_residency_confirmed && !taxResidencyConfirmed) {
          setDetectedCountry(fullUserData.country);
          setShowTaxResidencyModal(true);
          console.log('[GoogleAuth] Tax residency modal will be shown');
        } else {
          checkLocationChange();
        }
      } catch (profileError) {
        console.warn('[GoogleAuth] ⚠️ Could not load full profile, using basic data:', profileError);
      }
      
      setLoading(false);
      console.log('[GoogleAuth] ✅ Authentication complete and successful!');
      return latestUserData;
      
    } catch (error) {
      console.error('[GoogleAuth] ❌ Authentication failed during session exchange');
      console.error('[GoogleAuth] Error:', error.message);
      
      // Clear any partial state
      setAuthToken(null);
      setUser(null);
      setLoading(false);
      return false;
    }
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      localStorage.removeItem('auth_token');
      // Umzugs-Hinweis darf bei der nächsten Anmeldung wieder 1× erscheinen.
      try { sessionStorage.removeItem('ivoliq_migration_hint_shown'); } catch { /* noop */ }
      setAuthToken(null);
      setUser(null);
      resetAnalyticsUser();
    }
  };

  // Handle tax residency confirmation
  const handleTaxResidencyConfirm = (country) => {
    console.log('[AuthContext] handleTaxResidencyConfirm called with country:', country);
    
    // Set local flag to prevent modal from reopening
    setTaxResidencyConfirmed(true);
    
    // Close modal immediately
    setShowTaxResidencyModal(false);
    console.log('[AuthContext] Modal closed');
    
    // Update user state
    setUser(prev => ({
      ...prev,
      country,
      tax_residency_confirmed: true
    }));
    
    // Refresh user data from backend asynchronously
    setTimeout(async () => {
      try {
        const response = await axios.get(`${API}/auth/me`);
        setUser(response.data);
        console.log('[AuthContext] User data refreshed from backend');
      } catch (error) {
        console.error('[AuthContext] Failed to refresh user data:', error);
      }
    }, 1000);
  };

  // Handle location change update
  const handleLocationUpdate = (country) => {
    setShowLocationChangeModal(false);
    // Update user state
    setUser(prev => ({
      ...prev,
      country
    }));
  };

  // Handle location change dismiss
  const handleLocationChangeDismiss = () => {
    setShowLocationChangeModal(false);
  };

  // Function to refresh user data from backend
  const refreshUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      const userData = response.data;
      setUser(userData);
      if (userData.tax_residency_confirmed) {
        setTaxResidencyConfirmed(true);
        setShowTaxResidencyModal(false);
      }
      return userData;
    } catch (error) {
      console.error('Error refreshing user data:', error);
      throw error;
    }
  };

  const value = {
    user,
    authToken,
    loading,
    error,
    setError,
    register,
    login,
    loginWithGoogle,
    handleGoogleCallback,
    logout,
    resendVerification,
    refreshUser,
    startGuest,
    switchGuestCountry,
    switchGuestPersona,
    resumeGuest,
    sendResumeEmail,
    claimGuest,
    logGuestEvent,
    isAuthenticated: !!user,
    showInactivityWarning,
    extendSession,
    showTaxResidencyModal,
    showLocationChangeModal,
    detectedCountry,
    locationCheckData,
    handleTaxResidencyConfirm,
    handleLocationUpdate,
    handleLocationChangeDismiss,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
